import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Grid, InputAdornment, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useState } from 'react';
import { IUserGroup, userGroupsActions } from '../../../redux/userGroups/userGroupsSlice';
import SearchInput from '../../Common/SearchInput';
import CustomNoRowsOverlay from '../../Table/CustomNoRowsOverlay';
import { IColumn } from './columns';
import { RoundedSkeleton } from '../../Common/Skeleton';
import StyledBackButton from '../../Common/StyledBackButton';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CreateUpdateUserGroupModal from '../CreateUpdateUserGroupModal/CreateUpdateUserGroupModal';
import AddRemoveMemberModal from '../AddRemoveMemberModal';

const LoadingSkeleton = () => (
  <Grid height="max-height" px="24px" py="12px">
    {[...Array(5)].map((_) => (
      <Grid
        key={uuidv4()}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <RoundedSkeleton variant="rounded" width={400} height={14.875} />
        <RoundedSkeleton variant="rounded" width={400} height={14.875} />
        <RoundedSkeleton variant="rounded" width={50} height={14.875} />
      </Grid>
    ))}
  </Grid>
);

export interface PaginationModelChangeProps {
  pageSize: number;
  page: number;
}

export interface CellClickProps {
  field: string;
  row: {
    id: string;
  };
}

interface UserGroupsTableProps {
  handleOnCellClick?: (_params: CellClickProps) => void;
  handleOnPaginationModelChange: (_event: PaginationModelChangeProps) => void;
  rows: IUserGroup[];
  rowCount: number;
  loading: boolean;
  columns: IColumn[];
  handleOnChangeSearchTerm: (_v: string) => void;
  searchTerm: string;
  pageSize: number;
  page: number;
}

export default function UserGroupsTable({
  handleOnCellClick,
  handleOnPaginationModelChange,
  rows,
  rowCount,
  loading,
  columns,
  handleOnChangeSearchTerm,
  searchTerm,
  pageSize,
  page,
}: UserGroupsTableProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const getRowClassName = () => 'no-border';

  const [isCreateUserGroupModalOpen, setIsCreateUserGroupModalOpen] = useState(false);
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const { createUserGroupSuccess, addRemoveMembersSuccess, createdUserGroup } = useAppSelector(
    (state) => state.userGroups
  );

  useEffect(() => {
    if (createUserGroupSuccess) {
      setIsCreateUserGroupModalOpen(false);
      dispatch(userGroupsActions.resetCreateUserGroupSuccess());
      setIsAddMembersModalOpen(true);
    }
  }, [createUserGroupSuccess]);

  useEffect(() => {
    if (addRemoveMembersSuccess) {
      setIsAddMembersModalOpen(false);
      dispatch(userGroupsActions.resetAddRemoveMembersSuccess());
    }
  }, [addRemoveMembersSuccess]);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          bgcolor: '#FFF',
          boxShadow: theme.borderStyles.cardBorders,
          borderRadius: '5px',
          border: '1px solid #7CAEBE',
        }}
      >
        <Grid container p="20px" spacing={2}>
          <Grid item flexGrow={1}>
            <SearchInput
              type="text"
              placeholder={t('clientTable.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleOnChangeSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <img src="/assets/icons/magnifer.svg" alt="search" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <StyledBackButton
              variant="outlined"
              onClick={() => {
                setIsCreateUserGroupModalOpen(true);
              }}
              sx={{ width: 'max-contetnt' }}
            >
              <Box component="img" src="/assets/icons/user-plus.svg" alt="plus-icon" />
              <span>{t('topbar.newUserGroup')}</span>
            </StyledBackButton>
          </Grid>
        </Grid>
        <Box
          flexGrow="1"
          width="100%"
          height="calc(100vh - 174px)"
          sx={{
            '&::-webkit-scrollbar': {
              width: '0px',
              background: 'transparent',
            },
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
          }}
        >
          <DataGrid
            onPaginationModelChange={handleOnPaginationModelChange}
            onCellClick={handleOnCellClick}
            rows={rows}
            rowCount={rowCount}
            paginationMode="server"
            columns={columns as GridColDef[]}
            getRowClassName={getRowClassName}
            columnVisibilityModel={{ id: false }}
            pageSizeOptions={[5, 10, 15]}
            disableRowSelectionOnClick
            columnHeaderHeight={44}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize,
                  page,
                },
              },
            }}
            sx={{
              '& .MuiDataGrid-paginationToolbar': {
                justifyContent: 'space-between',
              },
              borderRight: 'none',
              borderLeft: 'none',
              borderBottom: 'none',
              borderColor: '#7CAEBE',
              borderRadius: '0px',
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                paddingLeft: '30px',
                borderColor: 'primary.light',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: () => theme.palette.primary.main,
                fontWeight: 500,
              },
              '& .MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                paddingLeft: '20px',
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderColor: '#7CAEBE',
              },
              '& .MuiDataGrid-footerContainer': {
                borderColor: '#7CAEBE',
              },
              '& .MuiTablePagination-root button': {
                mt: '4px',
                color: '#6CA3AC' /* Change text color */,
                '&:disabled': {
                  opacity: 0.5,
                },
              },
              '& .MuiTablePagination-input': {
                color: 'primary.main',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: '18px',
                mt: '4px',
                ml: 0,
                pl: 0,
              },
              '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
                color: '#7CAEBE',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18px',
              },
              '& .MuiTablePagination-displayedRows': {
                color: 'primary.main',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: '18px',
                mt: '4px',
              },
              '& .MuiDataGrid-virtualScroller': {
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '20px',
                  border: '3px solid #f5f5f5',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#888',
                },
              },
            }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: LoadingSkeleton,
            }}
          />
        </Box>
      </Box>
      <CreateUpdateUserGroupModal
        isOpen={isCreateUserGroupModalOpen}
        isCreateGroup
        initialGroupName={t('userGroups.createUpdateUserGroup.groupTitle')}
        handleDismiss={() => {
          setIsCreateUserGroupModalOpen(false);
        }}
      />

      {isAddMembersModalOpen && (
        <AddRemoveMemberModal
          isOpen={isAddMembersModalOpen}
          handleDismiss={() => setIsAddMembersModalOpen(false)}
          userGroupId={createdUserGroup.id}
          name={createdUserGroup.name}
          isAdd
        />
      )}
    </>
  );
}
